import type { Island, World } from '@cloudpearl/core';

/**
 * Pure typed-array builders for the open world's terrain — no three.js import, so they run in a
 * Web Worker (or synchronously where workers are unavailable) and transfer with zero copies.
 *
 * Terrain is a grid of 64 m tiles with four LODs (1, 2, 4, 8 m vertex spacing). Each tile carries
 * a normal texture sampled at its vertices: the mesh supplies the shape, the texture supplies a
 * smooth per-pixel normal (no triangle creases), the island coverage mask (the coastline is cut
 * per pixel) and a cavity term (valleys darker, crests brighter).
 */

export const TILE = 64;
export const LOD_STEP = [1, 2, 4, 8] as const;
export type Lod = 0 | 1 | 2 | 3;

const BORDER = 2;

export interface TileData {
  ti: number;
  tj: number;
  lod: Lod;
  /** Verts per side (= normal texture size). */
  n: number;
  /** Tile-local positions (origin = tile min corner, y absolute). */
  position: Float32Array;
  /** Normal-texture UV per vertex. */
  tile: Float32Array;
  index: Uint32Array;
  /** RGBA8 n×n: normal.xz, mask, cavity. */
  normalTex: Uint8Array;
  /** n×n (height, mask) pairs on the vertex grid — LOD 0 tiles feed the grass heightfield. */
  hm: Float32Array;
  minY: number;
  maxY: number;
  empty: boolean;
}

export function tileKey(ti: number, tj: number): number {
  return (ti + 32768) * 65536 + (tj + 32768);
}

const sample = { h: 0, hx: 0, hz: 0, mask: 0 };

export function buildTile(world: World, ti: number, tj: number, lod: Lod): TileData {
  const step = LOD_STEP[lod];
  const n = TILE / step + 1;
  const x0 = ti * TILE;
  const z0 = tj * TILE;
  const empty = (): TileData => ({
    ti,
    tj,
    lod,
    n,
    position: new Float32Array(0),
    tile: new Float32Array(0),
    index: new Uint32Array(0),
    normalTex: new Uint8Array(0),
    hm: new Float32Array(0),
    minY: 0,
    maxY: 0,
    empty: true,
  });
  // Sky tiles that no island can reach are empty.
  if (world.islands && world.islandsNear(x0 + TILE / 2, z0 + TILE / 2, [], TILE).length === 0) return empty();

  const g = n + BORDER * 2;
  const H = new Float32Array(g * g);
  const M = new Float32Array(g * g);
  const voidY = world.map.terrain.fallY - 20;
  for (let j = 0; j < g; j++) {
    const z = z0 + (j - BORDER) * step;
    for (let i = 0; i < g; i++) {
      const x = x0 + (i - BORDER) * step;
      world.ground(x, z, sample);
      H[j * g + i] = sample.h;
      M[j * g + i] = sample.mask;
    }
  }
  if (world.islands) {
    // Void samples take their neighbours' height so coastline quads don't plunge into the abyss.
    let anySolid = false;
    for (let k = 0; k < M.length; k++) if (M[k]! > 0.002) anySolid = true;
    if (!anySolid) return empty();
    for (let pass = 0; pass < 3; pass++) {
      for (let j = 0; j < g; j++) {
        for (let i = 0; i < g; i++) {
          const k = j * g + i;
          if (H[k]! > voidY) continue;
          let s = 0;
          let c = 0;
          for (const [di, dj] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
            const ii = i + di;
            const jj = j + dj;
            if (ii < 0 || jj < 0 || ii >= g || jj >= g) continue;
            const h = H[jj * g + ii]!;
            if (h > voidY) {
              s += h;
              c++;
            }
          }
          if (c) H[k] = s / c - 0.5;
        }
      }
    }
  }

  const position = new Float32Array(n * n * 3 + n * 4 * 3);
  const tile = new Float32Array(n * n * 2 + n * 4 * 2);
  const normalTex = new Uint8Array(n * n * 4);
  const hm = new Float32Array(n * n * 2);
  let minY = Infinity;
  let maxY = -Infinity;
  const inv2s = 1 / (2 * step);
  const cavK = 0.22 / Math.sqrt(step);
  for (let j = 0; j < n; j++) {
    for (let i = 0; i < n; i++) {
      const gi = i + BORDER;
      const gj = j + BORDER;
      const k = gj * g + gi;
      const h = H[k]!;
      const m = M[k]!;
      const v = j * n + i;
      position[v * 3] = i * step;
      position[v * 3 + 1] = h;
      position[v * 3 + 2] = j * step;
      tile[v * 2] = (i + 0.5) / n;
      tile[v * 2 + 1] = (j + 0.5) / n;
      hm[v * 2] = h;
      hm[v * 2 + 1] = m;
      if (m > 0.002 || !world.islands) {
        if (h < minY) minY = h;
        if (h > maxY) maxY = h;
      }
      const hx = (H[k + 1]! - H[k - 1]!) * inv2s;
      const hz = (H[k + g]! - H[k - g]!) * inv2s;
      const l = Math.sqrt(hx * hx + 1 + hz * hz);
      const nx = -hx / l;
      const nz = -hz / l;
      // Cavity: compare with the ring two samples out.
      const ring = (H[k + 2]! + H[k - 2]! + H[k + 2 * g]! + H[k - 2 * g]! + H[k + 1 + g]! + H[k - 1 - g]! + H[k + 1 - g]! + H[k - 1 + g]!) / 8;
      const cav = Math.min(1, Math.max(0, 0.62 + (h - ring) * cavK));
      normalTex[v * 4] = Math.round((nx * 0.5 + 0.5) * 255);
      normalTex[v * 4 + 1] = Math.round((nz * 0.5 + 0.5) * 255);
      normalTex[v * 4 + 2] = Math.round(Math.min(1, m) * 255);
      normalTex[v * 4 + 3] = Math.round(cav * 255);
    }
  }
  if (minY === Infinity) return empty();

  // Indices: drop quads with no coverage at all (open sky).
  const idx: number[] = [];
  const solid = (v: number): boolean => !world.islands || hm[v * 2 + 1]! > 0.002;
  for (let j = 0; j < n - 1; j++) {
    for (let i = 0; i < n - 1; i++) {
      const a = j * n + i;
      const b = a + 1;
      const c = a + n;
      const d = c + 1;
      if (!(solid(a) || solid(b) || solid(c) || solid(d))) continue;
      // Alternate the diagonal for a less directional triangulation.
      if ((i + j) & 1) idx.push(a, c, b, b, c, d);
      else idx.push(a, c, d, a, d, b);
    }
  }
  // Skirts hide cracks between neighbouring LODs.
  const skirt = lod === 0 ? 1.5 : step * 1.2;
  let sv = n * n;
  const edges: number[][] = [[], [], [], []];
  for (let i = 0; i < n; i++) {
    edges[0]!.push(i); // north edge (j = 0)
    edges[1]!.push((n - 1) * n + i); // south edge
    edges[2]!.push(i * n); // west edge
    edges[3]!.push(i * n + n - 1); // east edge
  }
  const flip = [false, true, true, false];
  edges.forEach((edge, e) => {
    const base = sv;
    for (const v of edge) {
      // Near a coastline a full skirt would hang out below the island's rock rim: keep it short.
      const sk = world.islands && !world.solidInset(x0 + position[v * 3]!, z0 + position[v * 3 + 2]!, skirt + 2.5) ? Math.min(skirt, 0.5) : skirt;
      position[sv * 3] = position[v * 3]!;
      position[sv * 3 + 1] = position[v * 3 + 1]! - sk;
      position[sv * 3 + 2] = position[v * 3 + 2]!;
      tile[sv * 2] = tile[v * 2]!;
      tile[sv * 2 + 1] = tile[v * 2 + 1]!;
      sv++;
    }
    for (let k = 0; k < edge.length - 1; k++) {
      const a = edge[k]!;
      const b = edge[k + 1]!;
      // Islands: only skirt fully-solid edges, so no flaps hang off the coastline.
      if (world.islands ? !(hm[a * 2 + 1]! > 0.99 && hm[b * 2 + 1]! > 0.99) : false) continue;
      const a2 = base + k;
      const b2 = base + k + 1;
      if (flip[e]) idx.push(a, a2, b, b, a2, b2);
      else idx.push(a, b, a2, b, b2, a2);
    }
  });
  if (idx.length === 0) return empty();
  return { ti, tj, lod, n, position, tile, index: new Uint32Array(idx), normalTex, hm, minY: minY - skirt, maxY, empty: false };
}

export function tileTransferables(t: TileData): ArrayBuffer[] {
  if (t.empty) return [];
  return [t.position.buffer, t.tile.buffer, t.index.buffer, t.normalTex.buffer, t.hm.buffer] as ArrayBuffer[];
}

// ------------------------------------------------------------------------------------------ islands

export interface UndersideData {
  id: number;
  cx: number;
  cz: number;
  position: Float32Array;
  normal: Float32Array;
  color: Float32Array;
  uv: Float32Array;
  depth: Float32Array;
  index: Uint32Array;
  radius: number;
}

/** sRGB hex → linear RGB (workers have no three.Color). */
export function hexToLinear(hex: string): [number, number, number] {
  const v = parseInt(hex.replace('#', ''), 16);
  const f = (c: number): number => {
    const s = c / 255;
    return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return [f((v >> 16) & 255), f((v >> 8) & 255), f(v & 255)];
}

function hash(n: number): number {
  const s = Math.sin(n * 127.1 + 311.7) * 43758.5453;
  return s - Math.floor(s);
}

function vnoise2(x: number, y: number, seed: number): number {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const fx = x - ix;
  const fy = y - iy;
  const ux = fx * fx * (3 - 2 * fx);
  const uy = fy * fy * (3 - 2 * fy);
  const h = (a: number, b: number): number => hash(a * 57.3 + b * 113.1 + seed * 7.7);
  const a = h(ix, iy);
  const b = h(ix + 1, iy);
  const c = h(ix, iy + 1);
  const d = h(ix + 1, iy + 1);
  return a + (b - a) * ux + (c - a) * uy + (a - b - c + d) * ux * uy;
}

/** Periodic-in-x fbm (period in samples) so the rock wraps seamlessly around the island. */
function rockNoise(u: number, t: number, period: number, seed: number): number {
  let s = 0;
  let a = 0.5;
  let f = 1;
  for (let o = 0; o < 4; o++) {
    const p = period * f;
    const x = u * p;
    const x0 = Math.floor(x);
    const fx = x - x0;
    // Wrap by blending the two lattice columns modulo the period.
    const n0 = vnoise2(((x0 % p) + p) % p, t * f * 3, seed + o);
    const n1 = vnoise2((((x0 + 1) % p) + p) % p, t * f * 3, seed + o);
    const nn = n0 + (n1 - n0) * (fx * fx * (3 - 2 * fx));
    s += a * nn;
    a *= 0.5;
    f *= 2;
  }
  return s / 0.9375;
}

/**
 * Rocky underside of a floating island, hung from its true coastline (where the coverage mask
 * crosses ½). The rim is a closed seal, so no line of sight can slip between turf and rock:
 * two rings tuck under the turf inside the coast, then a grassy lip rolls up just *above* the turf
 * edge a hand's width outside it (anything looking in under the grass hits rock), then a short
 * overhang, then the rock tapers to a keel with craggy, wrapped noise.
 * The coast is sampled by arc length (~1 m apart) so the rim follows every bay and bulge of the
 * warped coastline instead of cutting chords across them. aDepth: 0 at the rim → 1 at the keel.
 */
export function buildUnderside(world: World, isl: Island, colors: { cliff: string; cliffDark: string }): UndersideData {
  const R = 20;
  // `interior`: the march never left solid ground (this island merges into a neighbour there),
  // so the rim must stay buried under the neighbour's turf instead of poking through it.
  type Coast = { x: number; z: number; r: number; y: number; yTuck: number; yDeep: number; interior: boolean };
  const coast: Coast[] = [];
  const hAt = (dx: number, dz: number, r: number): number => world.height(isl.cx + dx * r, isl.cz + dz * r);
  const solid = (dx: number, dz: number, r: number): boolean => world.mask(isl.cx + dx * r, isl.cz + dz * r) >= 0.5;
  let prevR = 0;
  for (let a = 0; a < Math.PI * 2 - 1e-6; ) {
    const dx = Math.cos(a);
    const dz = Math.sin(a);
    const rMax = world.islandExtent(isl, dx, dz) * 1.25 + 12;
    // March out from just inside the previous direction's coast (falling back to deep inside).
    let r = prevR > 0 && solid(dx, dz, prevR - 6) ? prevR - 6 : isl.r * 0.45;
    while (r < rMax && solid(dx, dz, r)) r += 0.5;
    let interior = r >= rMax;
    let lo = r - 0.5;
    let hi = r;
    for (let it = 0; it < 6; it++) {
      const mid = (lo + hi) / 2;
      if (solid(dx, dz, mid)) lo = mid;
      else hi = mid;
    }
    r = (lo + hi) / 2;
    // A coastline that belongs to a neighbouring island (we merged into it) isn't ours to hang
    // rock from: pull the rim back under our own turf and bury it.
    if (!interior && world.islandDistance(isl, isl.cx + dx * r, isl.cz + dz * r) / isl.r > 1.12) interior = true;
    prevR = interior ? 0 : r;
    if (interior) r = isl.r * 0.62;
    // Turf height at the edge (the lip crests just above it), and the lowest turf over each tuck
    // ring (rims rise toward the edge, so the turf inside can be lower than at the coast).
    const y = Math.max(hAt(dx, dz, r), hAt(dx, dz, r - 0.4));
    const yTuck = Math.min(hAt(dx, dz, r - 0.5), hAt(dx, dz, r - 0.9), hAt(dx, dz, r - 1.4));
    const yDeep = Math.min(yTuck, hAt(dx, dz, r - 2.2), hAt(dx, dz, r - 3.2));
    coast.push({ x: dx, z: dz, r, y, yTuck, yDeep, interior });
    // ~1 m of coastline per sample.
    a += Math.min(0.06, Math.max(0.003, 1 / Math.max(r, 8)));
  }
  const K = coast.length;
  let meanY = 0;
  for (const c of coast) meanY += c.y;
  meanY /= K;
  // Arc length along the lip, for texture u (whole number of repeats around the island).
  const arc = new Float32Array(K + 1);
  for (let k = 1; k <= K; k++) {
    const p = coast[k - 1]!;
    const q = coast[k % K]!;
    arc[k] = arc[k - 1]! + Math.hypot(q.x * q.r - p.x * p.r, q.z * q.r - p.z * p.r);
  }
  const uRepeat = Math.max(1, Math.round(arc[K]! / 8));
  const cl = hexToLinear(colors.cliff);
  const cd = hexToLinear(colors.cliffDark);
  const seed = (isl.id % 997) * 0.37;
  const verts = (R + 1) * (K + 1) + 1;
  const position = new Float32Array(verts * 3);
  const color = new Float32Array(verts * 3);
  const uv = new Float32Array(verts * 2);
  const depth = new Float32Array(verts);
  const depthM = isl.depth;
  // Rings 0–3 form the rim seal; the taper starts at ring 4.
  const TAPER = 4;
  let v = 0;
  for (let ring = 0; ring <= R; ring++) {
    for (let k = 0; k <= K; k++) {
      const c = coast[k % K]!;
      const u = arc[k]! / arc[K]!;
      const tRing = Math.max(0, ring - TAPER + 1) / (R - TAPER + 1);
      const crag = rockNoise(u, tRing, 12, seed) - 0.5;
      // Interior (merged) directions: buried deep, so nothing shows through a neighbour's meadow.
      const bury = c.interior || coast[(k + 1) % K]!.interior || coast[(k + K - 1) % K]!.interior ? 12 : 0;
      let rad: number;
      let y: number;
      let t: number;
      if (ring === 0) {
        // Deep tuck: closes the rim well under the turf (LOD cracks near the coast see rock).
        rad = c.r - 3.2;
        y = c.yDeep - 0.7 - bury;
        t = 0;
      } else if (ring === 1) {
        // Shallow tuck, just under the turf.
        rad = c.r - 0.9;
        y = c.yTuck - 0.16 - bury;
        t = 0;
      } else if (ring === 2) {
        // The lip: rolls up just above the turf edge, a little outside the cut.
        rad = c.r + 0.3;
        y = c.y + 0.07 - bury;
        t = 0;
      } else if (ring === 3) {
        // Short overhang under the lip.
        rad = c.r + 0.8 + crag * 0.5;
        y = c.y - 0.75 - bury;
        t = 0.035;
      } else {
        const tt = (ring - TAPER + 1) / (R - TAPER + 1);
        const shrink = 1 - Math.pow(tt, 0.85) * 0.94;
        rad = (c.r + 0.7) * shrink * (1 + crag * 0.28 * Math.min(1, tt * 4));
        const rimBlend = Math.min(1, tt * 3);
        const top = c.y + (meanY - c.y) * rimBlend;
        y = top - 1.3 - Math.pow(tt, 1.2) * depthM * (0.92 + crag * 0.25) - bury * (1 - rimBlend);
        t = 0.035 + tt * 0.965;
      }
      position[v * 3] = isl.cx + c.x * rad;
      position[v * 3 + 1] = y;
      position[v * 3 + 2] = isl.cz + c.z * rad;
      const tone = 0.85 + crag * 0.5;
      const dk = Math.min(1, t * 1.3);
      color[v * 3] = (cl[0] + (cd[0] - cl[0]) * dk) * tone;
      color[v * 3 + 1] = (cl[1] + (cd[1] - cl[1]) * dk) * tone;
      color[v * 3 + 2] = (cl[2] + (cd[2] - cl[2]) * dk) * tone;
      uv[v * 2] = u * uRepeat;
      uv[v * 2 + 1] = t * depthM * 0.125;
      depth[v] = t;
      v++;
    }
  }
  // Keel tip.
  const tip = v;
  position[tip * 3] = isl.cx + (hash(seed) - 0.5) * isl.r * 0.2;
  position[tip * 3 + 1] = meanY - 1.2 - depthM * 1.08;
  position[tip * 3 + 2] = isl.cz + (hash(seed + 1) - 0.5) * isl.r * 0.2;
  color[tip * 3] = cd[0] * 0.8;
  color[tip * 3 + 1] = cd[1] * 0.8;
  color[tip * 3 + 2] = cd[2] * 0.8;
  depth[tip] = 1;
  const idx: number[] = [];
  const stride = K + 1;
  for (let ring = 0; ring < R; ring++) {
    for (let k = 0; k < K; k++) {
      const a = ring * stride + k;
      const b = a + 1;
      const c = a + stride;
      const d = c + 1;
      // Outward-facing: angle increases from +x toward +z, rings go down.
      idx.push(a, b, c, b, d, c);
    }
  }
  const last = R * stride;
  for (let k = 0; k < K; k++) idx.push(last + k, last + k + 1, tip);
  const index = new Uint32Array(idx);
  const normal = new Float32Array(verts * 3);
  for (let i = 0; i < index.length; i += 3) {
    const ia = index[i]! * 3;
    const ib = index[i + 1]! * 3;
    const ic = index[i + 2]! * 3;
    const ux = position[ib]! - position[ia]!;
    const uy = position[ib + 1]! - position[ia + 1]!;
    const uz = position[ib + 2]! - position[ia + 2]!;
    const wx = position[ic]! - position[ia]!;
    const wy = position[ic + 1]! - position[ia + 1]!;
    const wz = position[ic + 2]! - position[ia + 2]!;
    const nx = uy * wz - uz * wy;
    const ny = uz * wx - ux * wz;
    const nz = ux * wy - uy * wx;
    for (const o of [ia, ib, ic]) {
      normal[o] = normal[o]! + nx;
      normal[o + 1] = normal[o + 1]! + ny;
      normal[o + 2] = normal[o + 2]! + nz;
    }
  }
  // Seam vertices (k = 0 and k = K) share position: average their normals.
  for (let ring = 0; ring <= R; ring++) {
    const a = ring * stride * 3;
    const b = (ring * stride + K) * 3;
    for (let c = 0; c < 3; c++) {
      const s = normal[a + c]! + normal[b + c]!;
      normal[a + c] = s;
      normal[b + c] = s;
    }
  }
  for (let i = 0; i < verts; i++) {
    const l = Math.hypot(normal[i * 3]!, normal[i * 3 + 1]!, normal[i * 3 + 2]!) || 1;
    normal[i * 3] = normal[i * 3]! / l;
    normal[i * 3 + 1] = normal[i * 3 + 1]! / l;
    normal[i * 3 + 2] = normal[i * 3 + 2]! / l;
  }
  return { id: isl.id, cx: isl.cx, cz: isl.cz, position, normal, color, uv, depth, index, radius: isl.r * 1.7 + depthM };
}

export function undersideTransferables(u: UndersideData): ArrayBuffer[] {
  return [u.position.buffer, u.normal.buffer, u.color.buffer, u.uv.buffer, u.depth.buffer, u.index.buffer] as ArrayBuffer[];
}

/** Islands whose centre lies in tile (ti, tj) — each underside is built once, by its home tile. */
export function islandsOfTile(world: World, ti: number, tj: number): Island[] {
  const x0 = ti * TILE;
  const z0 = tj * TILE;
  return world.islandsNear(x0 + TILE / 2, z0 + TILE / 2, [], TILE).filter((isl) => Math.floor(isl.cx / TILE) === ti && Math.floor(isl.cz / TILE) === tj);
}
